import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { BlockMasterList, DistrictMasterList, StatesMasterList } from '../../models/geo.model';
import { CdnService } from '../../services/cdn.service';
import { GlobalService } from '../../services/global.service';
import { MetabaseService } from '../../services/metabase.service';
import { MetabaseDashboardComponent } from '../metabase-dashboard/metabase-dashboard.component';
import { MetabaseReportComponent } from '../metabase-report/metabase-report.component';
import { REPORT_IDS } from '../report.constants';

type AllocationLevel = 'state' | 'district' | 'block';

@Component({
    selector: 'metabase-dashboard-graph',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, MetabaseDashboardComponent, MatSelectModule],
    templateUrl: './metabase-dashboard-graph.component.html',
    styleUrl: './metabase-dashboard-graph.component.scss'
})
export class MetabaseDashboardGraphComponent extends MetabaseReportComponent implements OnInit {
    stateList: StatesMasterList[] = [];
    activeStateList: StatesMasterList[] = [];
    activeDistrictList: DistrictMasterList[] = [];
    activeBlockList: BlockMasterList[] = [];
    districtList: DistrictMasterList[] = [];
    blockList: BlockMasterList[] = [];
    selectedAllocationLevel: AllocationLevel = 'state';
    allocationLevels: AllocationLevel[] = ['state', 'district'];
    form = new FormGroup({
        state: new FormControl<string | null>(null),
        district: new FormControl<string | null>(null),
        block: new FormControl<string | null>(null)
    });
    showDistrictFilter = true;
    showBlockFilter = true;
    queryState: string | null = null;
    queryDistrict: string | null | undefined = null;
    queryBlock: string | null = null;
    queryAllocation: AllocationLevel | null = null;
    isPEModule = false;

    constructor(
        metabaseService: MetabaseService,
        private cdnService: CdnService,
        public override route: ActivatedRoute,
        private router: Router,
        public override globalService: GlobalService
    ) {
        super(metabaseService, route, globalService);
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.reportVisibliltyLevel();
        this.readQueryParams();
        if (this.reportId === REPORT_IDS['PE']) {
            this.isPEModule = true;
        }

        this.allocationLevels = this.getVisibleAllocationLevels();
        this.selectedAllocationLevel =
            this.queryAllocation && this.allocationLevels.includes(this.queryAllocation)
                ? this.queryAllocation
                : this.allocationLevels[0];

        if (this.activeUserLevel === 'national') {
            this.loadStateListAndSetup();
            this.showDistrictFilter = true;
        }
        if (this.activeUserLevel === 'state') {
            this.loadStateAndDistrictListForState();
            this.showDistrictFilter = true;
            const districtValue = this.form.value.district;
            this.filterParams = {
                state: this.form.value.state || null,
                district: districtValue && districtValue !== 'null' ? districtValue : null
            };
            this.initialParameters = this.filterParams as Record<string, string | string[]>;
        }
        if (this.activeUserLevel === 'district') {
            this.loadStateAndDistrictListForDistrict();
            this.showDistrictFilter = true;
        }
        if (this.activeUserLevel === 'block') {
            this.loadBlockList();
            this.showBlockFilter = true;
            this.showDistrictFilter = true;
        }

        this.form.controls['state'].valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((res) => {
            if (res && res !== 'null') {
                this.getDistrictListByStateName(res);
            } else {
                this.form.get('state')?.setValue(null, { emitEvent: false });
                this.districtList = [];
                this.form.get('district')?.setValue(null, { emitEvent: false });
                this.blockList = [];
                this.form.get('block')?.setValue(null, { emitEvent: false });
                this.initLoad();
            }
        });

        this.form.controls['district'].valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((res) => {
            if (res === 'null') {
                this.form.get('district')?.setValue(null, { emitEvent: false });
                this.blockList = [];
                this.form.get('block')?.setValue(null, { emitEvent: false });
                this.initLoad();
                return;
            }

            if (res && res !== 'null') {
                this.getBlockListByDistrictName(res);
                return;
            }

            this.initLoad();
        });

        this.form.controls['block'].valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((res) => {
            if (res === 'null') {
                this.form.get('block')?.setValue(null, { emitEvent: false });
            }

            this.initLoad();
        });
    }

    private readQueryParams(): void {
        const queryParams = this.route.snapshot.queryParamMap;
        this.queryState = this.normalizeQueryParam(queryParams.get('state'));
        this.queryDistrict = this.normalizeQueryParam(queryParams.get('district'));
        this.queryBlock = this.normalizeQueryParam(queryParams.get('block'));
        const allocation = this.normalizeQueryParam(queryParams.get('allocation'));
        this.queryAllocation =
            allocation === 'state' || allocation === 'district' || allocation === 'block' ? allocation : null;
    }

    private normalizeQueryParam(value: string | null): string | null {
        if (!value || value === 'null') {
            return null;
        }

        return value;
    }

    private getVisibleAllocationLevels(): AllocationLevel[] {
        if (this.activeUserLevel === 'national' || this.activeUserLevel === 'state') {
            return ['state', 'district', 'block'];
        }

        if (this.activeUserLevel === 'district') {
            return ['district', 'block'];
        }

        return ['block'];
    }

    setDefaultValues() {
        if (this.activeUserLevel === 'state' && this.stateList && this.stateList.length > 0) {
            this.form.get('state')?.setValue(this.stateList[0]?.state_name_en);
        }
    }

    private loadStateListAndSetup(): void {
        this.cdnService.getStates().subscribe({
            next: (res: StatesMasterList[]) => {
                this.activeStateList = res.map((ele: StatesMasterList) => ({
                    ...ele
                }));
                this.stateList = this.activeStateList;

                if (this.queryState) {
                    const stateMatch = this.stateList.find(
                        (state: StatesMasterList) => state.state_name_en === this.queryState
                    );
                    if (stateMatch) {
                        this.form.get('state')?.setValue(stateMatch.state_name_en, { emitEvent: false });
                        this.getDistrictListByStateName(stateMatch.state_name_en);
                        return;
                    }
                }

                this.initLoad();
            }
        });
    }

    private loadStateAndDistrictListForState(): void {
        this.cdnService.getStates().subscribe({
            next: (res: StatesMasterList[]) => {
                this.activeStateList = res.map((ele: StatesMasterList) => ({
                    ...ele
                }));

                this.stateList = this.activeStateList.filter(
                    (state: StatesMasterList) => state.state_id === Number(this.userData.stateId)
                );

                this.form.get('state')?.setValue(this.stateList[0]?.state_name_en, { emitEvent: false });

                if (this.userData.stateId) {
                    this.getDistrictListByStateIdWithCallback(this.userData.stateId, () => {
                        if (this.queryDistrict) {
                            const districtMatch = this.districtList.find(
                                (district: DistrictMasterList) => district.district_name_en === this.queryDistrict
                            );
                            if (districtMatch) {
                                this.form
                                    .get('district')
                                    ?.setValue(districtMatch.district_name_en, { emitEvent: false });
                            }
                        }

                        if (this.selectedAllocationLevel === 'block' && this.form.value.district) {
                            this.getBlockListByDistrictName(this.form.value.district);
                            return;
                        }

                        this.initLoad();
                    });
                } else {
                    this.initLoad();
                }
            }
        });
    }

    private loadStateAndDistrictListForDistrict(): void {
        this.cdnService.getStates().subscribe({
            next: (res: StatesMasterList[]) => {
                this.activeStateList = res.map((ele: StatesMasterList) => ({
                    ...ele
                }));
                this.stateList = this.activeStateList.filter(
                    (state: StatesMasterList) => state.state_id === Number(this.userData.stateId)
                );
                this.form.get('state')?.setValue(this.stateList[0]?.state_name_en, { emitEvent: false });

                if (this.userData.stateId) {
                    this.getDistrictListByStateIdWithCallback(this.userData.stateId, () => {
                        const selectedDistrict = this.form.get('district')?.value;
                        const districtId = this.districtList.find(
                            (ele: DistrictMasterList) => ele.district_name_en === selectedDistrict
                        )?.district_id;

                        if (districtId) {
                            this.getBlockListByStateAndDistrictIdWithCallback(
                                this.userData.stateId!,
                                districtId,
                                () => {
                                    this.initLoad();
                                }
                            );
                        } else {
                            this.initLoad();
                        }
                    });
                } else {
                    this.initLoad();
                }
            }
        });
    }

    private loadBlockList(): void {
        this.cdnService.getStates().subscribe({
            next: (res: StatesMasterList[]) => {
                this.activeStateList = res.map((ele: StatesMasterList) => ({
                    ...ele
                }));
                this.stateList = this.activeStateList.filter(
                    (state: StatesMasterList) => state.state_id === Number(this.userData.stateId)
                );
                this.form.get('state')?.setValue(this.stateList[0]?.state_name_en, { emitEvent: false });

                if (this.userData.stateId) {
                    this.getDistrictListByStateIdWithCallback(this.userData.stateId, () => {
                        const selectedDistrict = this.form.get('district')?.value;
                        const districtId = this.districtList.find(
                            (ele: DistrictMasterList) => ele.district_name_en === selectedDistrict
                        )?.district_id;

                        if (districtId) {
                            this.getBlockListByStateAndDistrictIdWithCallback(
                                this.userData.stateId!,
                                districtId,
                                () => {
                                    this.initLoad();
                                }
                            );
                        } else {
                            this.initLoad();
                        }
                    });
                } else {
                    this.initLoad();
                }
            }
        });
    }

    private getDistrictListByStateIdWithCallback(stateId: string | number, callback: () => void): void {
        this.cdnService.getDistricts(stateId).subscribe({
            next: (res: DistrictMasterList[]) => {
                this.activeDistrictList = res.map((ele: DistrictMasterList) => ({
                    ...ele
                }));
                this.districtList = this.activeDistrictList;

                if (this.activeUserLevel === 'district') {
                    this.districtList = this.activeDistrictList.filter(
                        (district: DistrictMasterList) => district.district_id === Number(this.userData.districtId)
                    );
                    this.form.get('district')?.setValue(this.districtList[0]?.district_name_en, { emitEvent: false });
                }

                if (this.activeUserLevel === 'block') {
                    this.districtList = this.activeDistrictList.filter(
                        (district: DistrictMasterList) => district.district_id === Number(this.userData.districtId)
                    );
                    this.form.get('district')?.setValue(this.districtList[0]?.district_name_en, { emitEvent: false });
                }

                callback();
            }
        });
    }

    private getBlockListByStateAndDistrictIdWithCallback(
        stateId: string | number,
        districtId: string | number,
        callback?: () => void
    ): void {
        this.cdnService.getBlocks(stateId, districtId).subscribe({
            next: (res: BlockMasterList[]) => {
                this.activeBlockList = res.map((ele: BlockMasterList) => ({
                    ...ele
                }));
                this.blockList = this.activeBlockList;

                if (this.activeUserLevel === 'block') {
                    this.blockList = this.activeBlockList.filter(
                        (block: BlockMasterList) => block.block_id === Number(this.userData.blockId)
                    );
                    this.form.get('block')?.setValue(this.blockList[0]?.block_name_en, { emitEvent: false });
                }

                if (this.queryBlock) {
                    const blockMatch = this.blockList.find(
                        (block: BlockMasterList) => block.block_name_en === this.queryBlock
                    );
                    if (blockMatch) {
                        this.form.get('block')?.setValue(blockMatch.block_name_en, { emitEvent: false });
                    }
                } else if (this.activeUserLevel !== 'block') {
                    this.form.get('block')?.setValue(null, { emitEvent: false });
                }

                callback?.();
            }
        });
    }

    getAllStateList() {
        this.cdnService.getStates().subscribe({
            next: (res: StatesMasterList[]) => {
                this.activeStateList = res.map((ele: StatesMasterList) => {
                    return {
                        ...ele
                    };
                });
            }
        });
    }

    getDistrictListByStateName(stateName: string): void {
        const stateId = this.stateList.find((ele: StatesMasterList) => ele.state_name_en === stateName)?.state_id;
        if (stateId) {
            this.cdnService.getDistricts(stateId).subscribe({
                next: (res: DistrictMasterList[]) => {
                    this.activeDistrictList = res.map((ele: DistrictMasterList) => ({
                        ...ele
                    }));
                    this.districtList = this.activeDistrictList;

                    const districtMatch = this.queryDistrict
                        ? this.districtList.find(
                              (district: DistrictMasterList) => district.district_name_en === this.queryDistrict
                          )
                        : null;
                    this.form
                        .get('district')
                        ?.setValue(districtMatch ? districtMatch.district_name_en : null, { emitEvent: false });

                    this.blockList = [];
                    this.form.get('block')?.setValue(null, { emitEvent: false });

                    this.initLoad();
                }
            });
        } else {
            this.districtList = [];
            this.form.get('district')?.setValue(null, { emitEvent: false });
            this.blockList = [];
            this.form.get('block')?.setValue(null, { emitEvent: false });
            this.initLoad();
        }
    }

    getBlockListByDistrictName(districtName: string): void {
        const selectedState = this.form.get('state')?.value;
        const stateId = this.stateList.find((ele: StatesMasterList) => ele.state_name_en === selectedState)?.state_id;
        const districtId = this.districtList.find(
            (ele: DistrictMasterList) => ele.district_name_en === districtName
        )?.district_id;

        if (stateId && districtId) {
            this.getBlockListByStateAndDistrictIdWithCallback(stateId, districtId, () => {
                if (this.activeUserLevel !== 'block') {
                    this.form.get('block')?.setValue(null, { emitEvent: false });
                }

                this.initLoad();
            });
        } else {
            this.blockList = [];
            this.form.get('block')?.setValue(null, { emitEvent: false });
            this.initLoad();
        }
    }

    getDistrictList(state: string): void {
        const stateId = this.stateList.find((ele: StatesMasterList) => ele.state_name_en === state)?.state_id;
        if (stateId) {
            this.cdnService.getDistricts(stateId).subscribe({
                next: (res: DistrictMasterList[]) => {
                    this.activeDistrictList = res.map((ele: DistrictMasterList) => {
                        return {
                            ...ele
                        };
                    });
                }
            });
        }
    }

    makeBlockData(reportId: number, reportName: string) {
        const districtValue = this.form.value.district;
        const blockValue = this.form.value.block;
        this.filterParams = {
            state: this.form.value.state || null,
            district: districtValue && districtValue !== 'null' ? districtValue : null,
            block: blockValue && blockValue !== 'null' ? blockValue : null
        };
        this.initialParameters = this.filterParams as Record<string, string | string[]>;

        this.router.navigate(['/dashboard-graph-meta', reportId, reportName], {
            queryParams: {
                state: this.form.value.state ?? '',
                district: this.form.value.district ?? '',
                block: this.form.value.block ?? '',
                allocation: 'block'
            }
        });
        return;
    }

    initLoad() {
        const stateValue = this.form.value.state;
        const districtValue = this.form.value.district;
        const blockValue = this.form.value.block;

        this.filterParams = {
            state: stateValue && stateValue !== 'null' ? stateValue : null,
            district: districtValue && districtValue !== 'null' ? districtValue : null,
            block: blockValue && blockValue !== 'null' ? blockValue : null
        };

        // !! Clean both filterParams and initialParameters - exclude undefined and string "null" values
        const cleanParams: Record<string, unknown> = {};
        Object.entries(this.filterParams).forEach(([key, value]) => {
            // !! We need the actually null value for the report to actually work(str null and undef excluded)
            if (value !== 'null' && value !== undefined) {
                cleanParams[key] = value;
            }
        });

        // !! Update filterParams for JWT token and initialParameters for URL
        this.filterParams = cleanParams;
        this.initialParameters = cleanParams as Record<string, string | string[]>;
        this.generateToken();
    }
}
